/**
 * Press page — two gimmick logos dueling across the full viewport.
 */
(function () {
  const A = window.BeyblingArena || {};
  const REDUCED = A.REDUCED ?? window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = A.clamp ?? ((v, min, max) => Math.max(min, Math.min(max, v)));
  const rand = A.rand ?? ((min, max) => min + Math.random() * (max - min));
  const measureArenaViewport =
    A.measureArenaViewport ??
    ((arena) => {
      const rect = arena.getBoundingClientRect();
      return { width: Math.max(rect.width, 1), height: Math.max(rect.height, 1) };
    });

  /** @type {Map<HTMLElement, () => void>} */
  const duelCleanups = new Map();

  function destroyDuel(arena) {
    if (!(arena instanceof HTMLElement)) return;
    const cleanup = duelCleanups.get(arena);
    if (cleanup) {
      cleanup();
      duelCleanups.delete(arena);
    }
    arena.querySelectorAll('.beybling-arena__logo').forEach((logo) => logo.remove());
    delete arena.dataset.beyblingArenaDuelReady;
  }

  function initDuel(arena) {
    if (arena.dataset.beyblingArenaDuelReady === 'true') return;

    const logoSrc = arena.dataset.logo;
    if (!logoSrc) return;

    const AIR = 0.988;
    const WALL_RESTITUTION = 0.98;
    const BODY_RESTITUTION = 0.95;
    const duelSpinDamping = 0.997;

    /** @type {Array<any>} */
    const bodies = [];
    let width = arena.clientWidth || 1;
    let height = arena.clientHeight || 1;
    let rafId = null;
    let running = false;
    let duelRipTimer = 0;
    /** @type {ResizeObserver | null} */
    let resizeObserver = null;

    const measure = () => {
      const size = measureArenaViewport(arena);
      width = size.width;
      height = size.height;
    };
    measure();

    const spawnBody = (x, y, r, vx, vy, va, angle) => {
      const el = document.createElement('img');
      el.className = 'beybling-arena__logo beybling-gimmick-logo';
      el.src = logoSrc;
      el.alt = '';
      el.draggable = false;
      el.style.width = el.style.height = r * 2 + 'px';
      arena.appendChild(el);
      bodies.push({ el, r, x, y, vx, vy, a: angle, va, dragging: false });
    };

    const r = clamp(Math.min(width, height) * 0.085, 48, 96);
    const pad = r + 24;
    const spin = REDUCED ? 0 : rand(22, 32);

    spawnBody(
      rand(pad, width * 0.34),
      rand(pad, height - pad),
      r,
      REDUCED ? 0 : rand(5, 10),
      REDUCED ? 0 : rand(-4, 4),
      spin,
      rand(0, 360)
    );
    spawnBody(
      rand(width * 0.66, width - pad),
      rand(pad, height - pad),
      r,
      REDUCED ? 0 : rand(-10, -5),
      REDUCED ? 0 : rand(-4, 4),
      -spin,
      rand(0, 360)
    );

    const paint = () => {
      for (const b of bodies) {
        b.el.style.transform = `translate(${(b.x - b.r).toFixed(2)}px, ${(b.y - b.r).toFixed(2)}px) rotate(${b.a.toFixed(2)}deg)`;
      }
    };

    if (REDUCED) {
      paint();

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(arena);
      }

      const cleanupReduced = () => {
        resizeObserver?.disconnect();
      };
      duelCleanups.set(arena, cleanupReduced);
      arena.dataset.beyblingArenaDuelReady = 'true';
      return;
    }

    const collide = (a, b) => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 0.0001;
      const minDist = a.r + b.r;
      if (dist >= minDist) return;

      const nx = dx / dist;
      const ny = dy / dist;
      const overlap = minDist - dist;
      a.x -= nx * overlap * 0.5;
      a.y -= ny * overlap * 0.5;
      b.x += nx * overlap * 0.5;
      b.y += ny * overlap * 0.5;

      const rvx = b.vx - a.vx;
      const rvy = b.vy - a.vy;
      const velAlongNormal = rvx * nx + rvy * ny;
      if (velAlongNormal > 0) return;

      const impulse = (-(1 + BODY_RESTITUTION) * velAlongNormal) / 2;
      a.vx -= impulse * nx;
      a.vy -= impulse * ny;
      b.vx += impulse * nx;
      b.vy += impulse * ny;

      const tangential = -rvx * ny + rvy * nx;
      a.va += tangential * 0.24;
      b.va -= tangential * 0.24;
    };

    const step = () => {
      if (!running) return;

      measure();
      duelRipTimer += 1;

      if (duelRipTimer % 90 === 0 && bodies.length === 2) {
        const [a, b] = bodies;
        for (const body of bodies) {
          const other = body === a ? b : a;
          if (Math.abs(body.va) < 22) body.va += rand(18, 30) * (body.va < 0 ? -1 : 1);
          const dx = other.x - body.x;
          const dy = other.y - body.y;
          const dist = Math.hypot(dx, dy) || 1;
          const kick = rand(3, 7);
          body.vx += (dx / dist) * kick;
          body.vy += (dy / dist) * kick;
        }
      }

      for (const b of bodies) {
        b.vx *= AIR;
        b.vy *= AIR;

        if (Math.abs(b.va) < 18) b.va += 0.45 * (b.va < 0 ? -1 : 1);

        b.x += b.vx;
        b.y += b.vy;

        if (b.x < b.r) {
          b.x = b.r;
          b.vx = Math.abs(b.vx) * WALL_RESTITUTION;
          b.va += Math.abs(b.vy) * 0.1;
        } else if (b.x > width - b.r) {
          b.x = width - b.r;
          b.vx = -Math.abs(b.vx) * WALL_RESTITUTION;
          b.va += Math.abs(b.vy) * 0.1;
        }
        if (b.y < b.r) {
          b.y = b.r;
          b.vy = Math.abs(b.vy) * WALL_RESTITUTION;
          b.va += Math.abs(b.vx) * 0.1;
        } else if (b.y > height - b.r) {
          b.y = height - b.r;
          b.vy = -Math.abs(b.vy) * WALL_RESTITUTION;
          b.va += Math.abs(b.vx) * 0.1;
        }

        b.a += b.va;
        b.va *= duelSpinDamping;
      }

      for (let pass = 0; pass < 6; pass++) {
        for (let i = 0; i < bodies.length; i++) {
          for (let j = i + 1; j < bodies.length; j++) collide(bodies[i], bodies[j]);
        }
      }

      paint();
      rafId = requestAnimationFrame(step);
    };

    const start = () => {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(step);
    };

    const stop = () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(arena);
    }

    document.addEventListener('visibilitychange', onVisibility);
    start();

    const cleanup = () => {
      stop();
      resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    duelCleanups.set(arena, cleanup);
    arena.dataset.beyblingArenaDuelReady = 'true';
  }

  const boot = () => {
    document.querySelectorAll('.beybling-arena--duel').forEach((arena) => {
      if (arena instanceof HTMLElement) initDuel(arena);
    });
  };

  const restartDuels = () => {
    document.querySelectorAll('.beybling-arena--duel').forEach((arena) => {
      if (arena instanceof HTMLElement) destroyDuel(arena);
    });
    boot();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.addEventListener('pagehide', () => {
    document.querySelectorAll('.beybling-arena--duel').forEach((arena) => {
      if (arena instanceof HTMLElement) destroyDuel(arena);
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (!document.body.classList.contains('beybling-press-duel')) return;

    const arena = document.querySelector('.beybling-arena--duel');
    if (!(arena instanceof HTMLElement)) return;

    const hasLogos = Boolean(arena.querySelector('.beybling-arena__logo'));
    const needsRestart =
      event.persisted ||
      !hasLogos ||
      (arena.dataset.beyblingArenaDuelReady === 'true' && !duelCleanups.has(arena));

    if (needsRestart) restartDuels();
  });
})();
